import React, { useMemo, useState, useEffect, useCallback } from "react";
import ResizableHeader from "../../shared/ResizableHeader";
import TablePagination from "../../shared/TablePagination";
import useAutoAdvancePagination from "../../shared/useAutoAdvancePagination";
import ExportLoader from "../../shared/ExportLoader";
import useReportExport from "../../shared/useReportExport";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../NavBars/ActionToolbar";
import GridDesign from "../../GridDesign/GridDesign";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useDbContext } from "../../../context/DbContext";

// ─── Default column definitions ───────────────────────────────────────────────
const getDefaultColumns = () => {
  const list = [
    { key: "date", label: "Date", width: 96 },
    { key: "particulars", label: "Particulars", width: 180 },
    { key: "accountNumber", label: "Account No", width: 110 },
    { key: "voucher", label: "Voucher", width: 90 },
    { key: "entryNo", label: "Entry No", width: 80, align: "center" },
    { key: "debit", label: "Debit", width: 110, align: "right" },
    { key: "credit", label: "Credit", width: 110, align: "right" },
    { key: "debitWt", label: "Debit Wt", width: 90, align: "right" },
    { key: "creditWt", label: "Credit Wt", width: 90, align: "right" },
    { key: "balance", label: "Balance", width: 110, align: "right" },
    { key: "balanceWt", label: "Balance Wt", width: 100, align: "right" },
  ];
  return list.map((col, idx) => ({
    ...col,
    columnName: col.key,
    header: col.label,
    orderNo: idx,
    show: true,
  }));
};

const FORM_NAME = "Ledger Report";

function LedgerReport({ onClose, ledgerReportData, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [gridColumns, setGridColumns] = useState(() => getDefaultColumns());
  const [showGridDesignModal, setShowGridDesignModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentOrderBy, setCurrentOrderBy] = useState(() => {
    const ob = ledgerReportData?.orderBy;
    if (ob) {
      const raw =
        typeof ob === "object" && ob.value
          ? ob.value
          : typeof ob === "object" && ob.label
            ? ob.label
            : String(ob);
      if (/entry/i.test(raw)) return "Order By Entry No";
    }
    return "Order By Date";
  });

  // ── Load grid column settings ──────────────────────────────────────────────
  useEffect(() => {
    const loadGridSettings = async () => {
      if (!dbCode) return;
      const defaults = getDefaultColumns();

      const cached = localStorage.getItem(`grid_design_${FORM_NAME}`);
      if (cached) {
        try {
          setGridColumns(JSON.parse(cached));
          return;
        } catch (e) {}
      }

      try {
        const res = await axios.get(
          `${apiBaseUrl}/main/griddesign/${dbCode}/${FORM_NAME}`,
        );
        const dbCols = res.data.recordset || res.data || [];
        if (dbCols.length > 0) {
          const merged = defaults.map((initCol) => {
            const m = dbCols.find(
              (c) =>
                String(c.ColName).toLowerCase() ===
                String(initCol.columnName).toLowerCase(),
            );
            return m
              ? {
                  ...initCol,
                  width: m.Width ?? initCol.width,
                  orderNo: m.InNo ?? initCol.orderNo,
                  header: m.Header || initCol.header,
                  show: m.Vis === 1,
                }
              : initCol;
          });
          merged.sort((a, b) => a.orderNo - b.orderNo);
          localStorage.setItem(
            `grid_design_${FORM_NAME}`,
            JSON.stringify(merged),
          );
          setGridColumns(merged);
        } else {
          setGridColumns(defaults);
        }
      } catch {
        setGridColumns(defaults);
      }
    };
    loadGridSettings();
  }, [dbCode, apiBaseUrl]);

  // ── Visible columns & grid template ───────────────────────────────────────
  const visibleColumns = useMemo(
    () => gridColumns.filter((col) => col.show),
    [gridColumns],
  );
  const gridTemplateColumns = useMemo(
    () => visibleColumns.map((col) => `${col.width}px`).join(" "),
    [visibleColumns],
  );

  // ── Fetch report data ──────────────────────────────────────────────────────
  const fetchData = async (orderByOverride) => {
    try {
      setIsLoading(true);
      setErrorMsg("");
      const activeOrderBy = orderByOverride !== undefined ? orderByOverride : currentOrderBy;
      const response = await axios.post(
        `${apiBaseUrl}/main/ledger_Report/${dbCode}`,
        {
          ...ledgerReportData,
          orderBy: activeOrderBy,
        },
      );
      setTableData(response.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setErrorMsg("Error connecting to server. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dbCode) fetchData();
  }, [apiBaseUrl, ledgerReportData, dbCode]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  // ── Number formatter ───────────────────────────────────────────────────────
  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || value === "") return "";
    const n = Number(value);
    if (isNaN(n)) return "";
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // ── Balance formatter (handles "1500.000 Dr", "500.00 Cr", or numbers) ─────
  const formatBalance = (value, decimals = 2) => {
    if (value === null || value === undefined || value === "") return "";
    const str = String(value).trim();
    const match = str.match(/^([+-]?[\d,]+(?:\.\d+)?)\s*(Dr|Cr)?$/i);
    if (match) {
      const rawNumStr = match[1].replace(/,/g, "");
      const num = parseFloat(rawNumStr);
      if (isNaN(num)) return str;
      const formattedNum = num.toLocaleString("en-IN", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      return match[2] ? `${formattedNum} ${match[2]}` : formattedNum;
    }
    return str;
  };

  // ── Row value mapper ───────────────────────────────────────────────────────
  const formatValue = (row, key) => {
    switch (key) {
      case "date":
        return row.Date || "";
      case "particulars":
        return row.Particulars || "";
      case "accountNumber":
        return row.AccountNumber || "";
      case "voucher":
        return row.Voucher || "";
      case "entryNo":
        return row.EntryNo ?? "";
      case "debit":
        return formatNumber(row.Debit, 2);
      case "credit":
        return formatNumber(row.Credit, 2);
      case "debitWt":
        return formatNumber(row.Debitwt, 3);
      case "creditWt":
        return formatNumber(row.Creditwt, 3);
      case "balance":
        return formatBalance(row.Balance, 2);
      case "balanceWt":
        return formatBalance(row.Balancewt, 3);
      default:
        return "";
    }
  };

  // ── Client-side quick search ───────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return tableData;
    const query = searchQuery.toLowerCase().trim();
    return tableData.filter((row) =>
      visibleColumns.some((col) =>
        String(formatValue(row, col.key)).toLowerCase().includes(query),
      ),
    );
  }, [tableData, searchQuery, visibleColumns]);

  // ── Dynamic footer totals based on filtered rows ───────────────────────────
  // Only sum actual transaction rows — exclude special summary rows returned by the SP.
  const SPECIAL_ROWS = ["Opening Balance", "Closing Balance", "Total"];

  // Keys that are simple sums (debit/credit and their weights)
  const sumKeys = ["debit", "credit", "debitWt", "creditWt"];

  // Raw value extractor — reads the raw number from the row, NOT the formatted display string
  // so that parseFloat doesn't choke on comma-formatted values like "1,23,456.00"
  const getRawValue = (row, key) => {
    switch (key) {
      case "debit":    return Number(row.Debit    ?? 0);
      case "credit":   return Number(row.Credit   ?? 0);
      case "debitWt":  return Number(row.Debitwt  ?? 0);
      case "creditWt": return Number(row.Creditwt ?? 0);
      default: return 0;
    }
  };

  const dynamicTotals = useMemo(() => {
    // Filter out special summary rows before summing
    const txnRows = filteredRows.filter(
      (row) => !SPECIAL_ROWS.includes(row.Particulars)
    );

    const sums = txnRows.reduce((acc, row) => {
      sumKeys.forEach((key) => {
        const v = getRawValue(row, key);
        acc[key] = (acc[key] || 0) + (isNaN(v) ? 0 : v);
      });
      return acc;
    }, {});

    // For Balance/BalanceWt: show the value from the last Closing Balance row,
    // or fall back to the last transaction row's balance.
    const closingRow = [...filteredRows].reverse().find(
      (row) => row.Particulars === "Closing Balance"
    );
    const lastRow = txnRows[txnRows.length - 1];
    sums.balance   = closingRow?.Balance || lastRow?.Balance || "";
    sums.balanceWt = closingRow?.Balancewt || lastRow?.Balancewt || "";

    return sums;
  }, [filteredRows]);

  const formatFooterValue = (key) => {
    if (key === "balance") return formatBalance(dynamicTotals.balance, 2);
    if (key === "balanceWt") return formatBalance(dynamicTotals.balanceWt, 3);
    if (key === "debitWt" || key === "creditWt") return formatNumber(dynamicTotals[key] || 0, 3);
    return formatNumber(dynamicTotals[key] || 0, 2);
  };


  const {
    currentPage,
    pageSize,
    pageSizeOptions,
    totalPages,
    totalRows,
    displayedRows,
    selectedIndex,
    setSelectedIndex,
    tableContainerRef,
    handleScroll,
    handleWheel,
    handleKeyDown,
    handlePageChange,
    handlePageSizeChange,
    autoAdvance,
    toggleAutoAdvance,
    isFlipping,
  } = useAutoAdvancePagination({
    items: filteredRows,
    initialPageSize: 100,
    pageSizeOptions: [50, 100, 250, 500],
    initialAutoAdvance: true,
  });

  const { exportingText, runExport } = useReportExport();

  // ── Export CSV ─────────────────────────────────────────────────────────────
  const exportToCsv = () => {
    runExport("Generating Excel / CSV...", () => {
      const header = visibleColumns.map((col) => col.header).join(",");
      const rows = filteredRows.map((row) =>
        visibleColumns
          .map(
            (col) => `"${String(formatValue(row, col.key)).replace(/"/g, '""')}"`,
          )
          .join(","),
      );
      const allNumericKeys = ["debit", "credit", "debitWt", "creditWt", "balance", "balanceWt"];
      const totalRow = visibleColumns
        .map((col) => {
          if (col.key === "particulars") return '"Total"';
          if (allNumericKeys.includes(col.key))
            return `"${formatFooterValue(col.key)}"`;
          return '""';
        })
        .join(",");

      const csv = [header, ...rows, totalRow].join("\n");
      const link = document.createElement("a");
      link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
      link.download = "ledger_report.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  // ── Export PDF ─────────────────────────────────────────────────────────────
  const exportToFullPDF = () => {
    runExport("Generating PDF Report...", () => {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a3",
      });
      const columns = visibleColumns.map((col, idx) => ({
        header: col.header,
        dataKey: `col${idx + 1}`,
      }));
      const rows = filteredRows.map((item) => {
        const row = {};
        visibleColumns.forEach((col, idx) => {
          row[`col${idx + 1}`] = formatValue(item, col.key);
        });
        return row;
      });
      const allNumericKeys = ["debit", "credit", "debitWt", "creditWt", "balance", "balanceWt"];
      const totalRow = {};
      visibleColumns.forEach((col, idx) => {
        if (col.key === "particulars") totalRow[`col${idx + 1}`] = "Total";
        else if (allNumericKeys.includes(col.key))
          totalRow[`col${idx + 1}`] = formatFooterValue(col.key);
        else totalRow[`col${idx + 1}`] = "";
      });
      rows.push(totalRow);

      doc.text("Ledger Report", 15, 20);
      doc.autoTable({
        columns,
        body: rows,
        startY: 30,
        margin: { top: 30 },
        styles: { fontSize: 8 },
      });
      doc.save("ledger_report.pdf");
    });
  };

  // Stable column resize handler (shared ResizableHeader imported at module scope)
  const handleColumnResize = useCallback((key, newWidth, name) => {
    setGridColumns((prev) => {
      const updated = prev.map((c) => (c.key === key ? { ...c, width: newWidth } : c));
      localStorage.setItem(`grid_design_${name}`, JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <div
      className="bg-white mt-[2vh] sm:mt-0 font-normal w-[98vw] h-[96vh] rounded-[5px] shadow-md flex flex-col items-center absolute transition-all duration-300 md:w-[98%] md:h-[98%]"
      style={{ zIndex }}
    >
      <style>{`
        .lr-border { border: 1px solid #ffffff; }
        .report-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .report-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
        .report-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
        .report-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
      `}</style>

      {/* ── Window Header ── */}
      <WindowHeader
        onClose={onClose}
        title="Ledger Report"
        logo={headeLogo}
        style={{ height: "30px" }}
        className="bg-[#0f56d3] text-white"
        titleClassName="text-xs"
        closeBtnClassName="w-6 h-6 rounded-[5px] border-none mr-2 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white"
      />

      {/* ── Action Toolbar ── */}
      <ActionToolbar
        size="small"
        iconSize={16}
        buttonHeight="36px"
        showSave={false}
        showPrint
        showClear={false}
        showFind={false}
        showEdit={false}
        showDelete={false}
        showExit
        showPdf
        showExcel
        showSettingsBar
        showRefresh
        showNavigation={false}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        onPrint={() => window.print()}
        onExcel={exportToCsv}
        onPdf={exportToFullPDF}
        onExit={onClose}
        settingsOptions={[
          {
            label: "Design Columns",
            onClick: () => setShowGridDesignModal(true),
          },
        ]}
      />

      {/* ── Body ── */}
      <div className="w-full flex-1 min-h-0 flex flex-col bg-white">
        {/* Info panel + Search bar */}
        <div className="w-full border-b border-gray-200 bg-gradient-to-r from-slate-50 to-blue-50/20 px-4 py-2 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-['Inter'] text-slate-700 flex-1">
            {/* Period */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
              <svg
                className="w-3.5 h-3.5 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span className="font-semibold text-slate-500">Period:</span>
              <span className="font-medium text-slate-900">
                {ledgerReportData?.fromDate || "-"} to{" "}
                {ledgerReportData?.toDate || "-"}
              </span>
            </div>

            {/* Ledger */}
            {ledgerReportData?.ledger?.label && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <svg
                  className="w-3.5 h-3.5 text-indigo-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                <span className="font-semibold text-slate-500">Ledger:</span>
                <span className="font-medium text-indigo-700">
                  {ledgerReportData.ledger.label}
                </span>
              </div>
            )}

            {/* Report Type */}
            {ledgerReportData?.reportType?.label && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <svg
                  className="w-3.5 h-3.5 text-emerald-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H3a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2z"
                  />
                </svg>
                <span className="font-semibold text-slate-500">Type:</span>
                <span className="font-medium text-emerald-700">
                  {ledgerReportData.reportType.label}
                </span>
              </div>
            )}

            {/* Branch */}
            {ledgerReportData?.branch?.label && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <svg
                  className="w-3.5 h-3.5 text-amber-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="font-semibold text-slate-500">Branch:</span>
                <span className="font-medium text-amber-700">
                  {ledgerReportData.branch.label}
                </span>
              </div>
            )}
            {/* Order By Filter */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
              <svg
                className="w-3.5 h-3.5 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                />
              </svg>
              <span className="font-semibold text-slate-500">Order By:</span>
              <select
                value={currentOrderBy}
                onChange={(e) => {
                  const val = e.target.value;
                  setCurrentOrderBy(val);
                  fetchData(val);
                }}
                className="bg-transparent text-slate-800 font-semibold text-[11px] outline-none cursor-pointer pr-1"
              >
                <option value="Order By Date">Order By Date</option>
                <option value="Order By Entry No">Order By Entry No</option>
              </select>
            </div>
          </div>

          {/* Quick Search */}
          <div className="flex items-center shrink-0">
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Search across all columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-7 py-1.5 text-xs border border-slate-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-['Inter'] font-semibold text-slate-800 bg-white shadow-sm transition-all placeholder-slate-400"
              />
              <svg
                className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 text-xs font-semibold px-1"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Grid and Loader */}
        <div
          ref={tableContainerRef}
          className="w-full flex-1 min-h-0 overflow-auto text-black text-xs font-semibold report-scroll outline-none focus:ring-1 focus:ring-blue-400"
          tabIndex={0}
          onScroll={handleScroll}
          onWheel={handleWheel}
          onKeyDown={handleKeyDown}
        >
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center bg-white/70">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-4 border-[#0f56d3] border-t-transparent rounded-full animate-spin" />
                <span className="font-['Inter'] text-[13px] text-gray-600 font-medium">
                  Fetching Report Data...
                </span>
              </div>
            </div>
          ) : errorMsg ? (
            <div className="w-full h-full flex items-center justify-center bg-white">
              <span className="font-['Inter'] text-[13px] text-red-500 font-medium">
                {errorMsg}
              </span>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center bg-white">
              <span className="font-['Inter'] text-[13px] text-gray-400 font-medium">
                No records found for the selected filters.
              </span>
            </div>
          ) : (
            <div className="min-w-max flex flex-col min-h-full">
              {/* Header Row */}
              <div className="sticky top-0 z-10 bg-blue-600 lr-border shrink-0">
                <div className="grid gap-0 h-8" style={{ gridTemplateColumns }}>
                  {visibleColumns.map((col) => (
                    <div
                      key={col.key}
                      className="text-white truncate text-center font-semibold font-['Inter'] lr-border text-xs w-full h-full flex items-center justify-center"
                    >
                      <ResizableHeader col={col} formName={FORM_NAME} onResize={handleColumnResize}>{col.header}</ResizableHeader>
                    </div>
                  ))}
                </div>
              </div>

              {/* Data Rows */}
              <div className="bg-gray-100 flex-1">
                {displayedRows.map((row, rowIndex) => {
                  const isClosing = row.Particulars === "Closing Balance";
                  const isTotal = row.Particulars === "Total";
                  const isSelected = rowIndex === selectedIndex;
                  return (
                    <div
                      key={rowIndex}
                      data-row-index={rowIndex}
                      onClick={() => setSelectedIndex(rowIndex)}
                      className={`grid gap-0 h-8 border-b border-gray-300 hover:bg-blue-50/40 cursor-pointer transition-colors duration-150 ${
                        isSelected
                          ? "bg-blue-100 font-bold text-blue-900 ring-1 ring-inset ring-blue-500"
                          : isClosing
                            ? "bg-red-50 text-red-700 font-bold"
                            : isTotal
                              ? "bg-[#dbeafe] text-blue-800 font-bold"
                              : rowIndex % 2 === 0
                                ? "bg-[#f7f7f7]"
                                : "bg-[#eeeeee]"
                      }`}
                      style={{ gridTemplateColumns }}
                    >
                      {visibleColumns.map((col) => (
                        <div
                          key={col.key}
                          className={`font-['Inter'] text-[11px] font-medium lr-border px-2 flex items-center truncate ${
                            col.align === "right"
                              ? "justify-end text-right"
                              : col.align === "center"
                                ? "justify-center text-center"
                                : ""
                          }`}
                        >
                          {formatValue(row, col.key)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Footer Totals Row */}
              <div
                className="grid gap-0 h-8 bg-[#dbeafe] text-blue-800 font-bold sticky bottom-0 z-10 shrink-0"
                style={{ gridTemplateColumns }}
              >
                {visibleColumns.map((col, i) => {
                  const allNumericKeys = ["debit", "credit", "debitWt", "creditWt", "balance", "balanceWt"];
                  const isNum = allNumericKeys.includes(col.key);
                  return (
                    <div
                      key={col.key}
                      className={`font-['Inter'] text-[11px] lr-border px-2 flex items-center truncate ${
                        isNum
                          ? "justify-end text-right"
                          : i === 0
                            ? "justify-start"
                            : ""
                      }`}
                    >
                      {i === 0
                        ? "Total"
                        : isNum
                          ? formatFooterValue(col.key)
                          : ""}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <TablePagination
          currentPage={currentPage}
          totalRows={totalRows}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          autoAdvance={autoAdvance}
          onToggleAutoAdvance={toggleAutoAdvance}
          isFlipping={isFlipping}
        />
      </div>

      {/* Grid Design Modal */}
      {showGridDesignModal && (
        <GridDesign
          onClose={() => setShowGridDesignModal(false)}
          onSave={(updated) => {
            setGridColumns(updated);
            setShowGridDesignModal(false);
          }}
          formName={FORM_NAME}
          initialColumns={getDefaultColumns()}
          zIndex={zIndex}
        />
      )}

      {/* Global Export Loader for Heavy Operations */}
      <ExportLoader message={exportingText} />
    </div>
  );
}

export default LedgerReport;
